import { useCallback, useContext, useMemo, useRef } from 'react';
import { useStore } from 'react-redux';
import _get from 'lodash/get';
import { useDataIsland } from 'reaxl-context';
import { getDataLayer, getConfigByBrand } from 'reaxl-analytics-handlers';
import { useDevice } from '@atc/react-device';
import { dispatchAnalyticsEvent } from '../analyticsEventDispatcher';
import AnalyticsContext from '../context/Context';
import { sendClick, sendEvent as eventBusSendEvent, sendImpressions, sendPage, sendThirdPartyEvent, sendDomReadyEvent } from '../analyticsEvents/sendAnalytics.js';
const noop = () => ({});
const defaultEvents = {
  sendClick: noop,
  sendEvent: noop,
  sendImpressions: noop,
  sendPage: noop,
  sendThirdPartyEvent: noop,
  sendDomReadyEvent: noop
};

// Corresponding values mapping from dataLayer to dataIsland
// This can be removed after dataIsland is removed everywhere
const dataLayerToDataIsland = {
  pg: 'pg',
  brandPg: 'pg_id',
  testName: 'test_name',
  experimentBoosts: 'experimentBoosts'
};
const sendEvent = async (_ref, _ref2) => {
  let {
    dataIslandContext,
    store,
    device,
    analyticsContext
  } = _ref;
  let {
    type,
    event,
    name,
    data,
    ...rest
  } = _ref2;
  if (event && typeof event.persist === 'function') {
    event.persist();
  }
  try {
    // TODO: analyticsDataIsland is replacing dataIslandContext
    const {
      dataLayer = () => {},
      events,
      option = {},
      history,
      dataIsland: analyticsDataIsland
    } = analyticsContext;
    const metadata = {
      event,
      data,
      dataLayer: getDataLayer(dataLayer() ?? {}),
      state: store ? store.getState() : {},
      device,
      history,
      ...rest
    };

    // set brand
    const brand = _get(metadata.state, 'brand', 'atc');
    option.brand = option.customBrand || brand;
    option.initialConfig = getConfigByBrand(option.brand);
    if (option.customBrand) {
      // for syndicated websites, we shouldn't need to worry about the page events
      // we will send impression/click events regardlessly
      option.skipPageEvent = true;
    }
    let dataIsland = {};
    if (analyticsDataIsland && typeof analyticsDataIsland === 'function') {
      metadata.dataIsland = analyticsDataIsland();
    } else {
      // get dataisland from either dataIsland Context or redux state
      // this will be replaced by dataLayer
      if (_get(metadata, 'dataIsland')) {
        dataIsland = {
          ...metadata.dataIsland
        };
      } else {
        dataIsland = _get(metadata, 'state.birf', {});
      }
      if (type === 'page') {
        metadata.dataIsland = dataIsland;
        dataIslandContext.setDataIsland(dataIsland);
      } else {
        // only use dataIslandContext if dataIsland is defined
        metadata.dataIsland = dataIslandContext.getDataIsland() ? dataIslandContext.getDataIsland() : dataIsland;
      }
    }

    // Add pg, brandPg, test_name, experimentBoosts to dataLayer for backwards compatibility.
    // This can be removed after dataIsland is removed everywhere
    // TODO: Why do we keep track of both ATC/KBB pg values????
    ['pg', 'brandPg', 'testName', 'experimentBoosts'].forEach(key => {
      if (metadata.dataLayer?.page && !metadata.dataLayer.page?.[key]) {
        metadata.dataLayer.page[key] = metadata.dataIsland?.page?.BIRF?.[dataLayerToDataIsland[key] || key];
      }
    });
    let response = {};

    // If third party event, skip analytics calls and dispatch custom event
    if (type === 'thirdparty' || type === 'page-dom-ready') {
      if (type === 'thirdparty' && !name) {
        throw new Error(`Analytics: ${name} is required for third party event.`);
      }
    } else {
      if (typeof events[name] !== 'function' && typeof name !== 'function') {
        return;
      }
      if (typeof name === 'function') {
        response = await name(metadata, option);
      } else {
        // Anything that's returned from the event call will be sent with metadata when we dispatch AnalyticsEvent
        // a response object is required for any third party tags that need additional reference
        response = await events[name](metadata, option);
      }
      if (!response) {
        throw new Error(`Analytics: ${name} did not return any response object.`);
      }
    }
    const eventData = {
      ...metadata,
      name,
      data: {
        ...metadata.data,
        ...response
      }
    };
    dispatchAnalyticsEvent(type, eventData);
    if (type === 'page' && data) {
      if (data.isInitialLoad) {
        // We are going to fire additional event for initial load
        // so we can easily handle tags in Launch that should fire once per page
        dispatchAnalyticsEvent('pageReady', eventData);
      } else {
        // TODO: This event is not ready to use yet! test in progress...
        dispatchAnalyticsEvent('page-new-search', eventData);
      }
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(err);
  }
};
export default function useAnalytics(appData) {
  if (appData === void 0) {
    appData = {};
  }
  const analyticsContext = useContext(AnalyticsContext);
  const {
    dataLayer = () => {}
  } = analyticsContext;
  const {
    useDevice: useDeviceHook = noop,
    useDataIsland: useDataIslandHook = noop
  } = _get(analyticsContext, 'hooks', {});

  // contextDevice is deprecated
  const contextDevice = useDevice();
  const device = useDeviceHook();

  // contextDataIsland is deprecated
  const contextDataIsland = useDataIsland();
  const dataIsland = useDataIslandHook();
  const {
    store: appDataStore,
    ...restAppData
  } = appData;
  let store = useStore();
  if (appDataStore) {
    store = appDataStore;
  }
  const restAppDataRef = useRef({});
  const ctxRef = useRef({});
  restAppDataRef.current = restAppData;
  ctxRef.current = {
    dataIslandContext: {
      ...contextDataIsland,
      ...dataIsland
    },
    store,
    device: {
      ...contextDevice,
      ...device
    },
    analyticsContext
  };
  const {
    disabled
  } = analyticsContext;
  const sendClickHandle = useCallback(function (name, event, data) {
    for (var _len = arguments.length, rest = new Array(_len > 3 ? _len - 3 : 0), _key = 3; _key < _len; _key++) {
      rest[_key - 3] = arguments[_key];
    }
    sendEvent(ctxRef.current, {
      type: 'click',
      event,
      name,
      data,
      ...restAppDataRef.current,
      ...rest
    });
  }, []);
  const sendEventHandle = useCallback(function (_temp) {
    let {
      type = 'event',
      event,
      name,
      data,
      ...rest
    } = _temp === void 0 ? {} : _temp;
    sendEvent(ctxRef.current, {
      type,
      event,
      name,
      data,
      ...restAppDataRef.current,
      ...rest
    });
  }, []);
  const sendImpressionsHandle = useCallback(_ref3 => {
    let {
      name,
      data,
      ...rest
    } = _ref3;
    sendEvent(ctxRef.current, {
      type: 'impression',
      name,
      data,
      ...restAppDataRef.current,
      ...rest
    });
  }, []);
  const sendPageHandle = useCallback(_ref4 => {
    let {
      data,
      isInitialLoad,
      includePageName = true,
      ...rest
    } = _ref4;
    sendEvent(ctxRef.current, {
      type: 'page',
      name: 'page',
      data: {
        ...data,
        isInitialLoad,
        includePageName
      },
      ...restAppDataRef.current,
      ...rest
    });
  }, []);
  const sendThirdPartyEventHandle = useCallback(_ref5 => {
    let {
      name,
      data,
      ...rest
    } = _ref5;
    sendEvent(ctxRef.current, {
      type: 'thirdparty',
      name,
      data,
      ...restAppDataRef.current,
      ...rest
    });
  }, []);
  const sendDomReadyEventHandle = useCallback(data => {
    sendEvent(ctxRef.current, {
      type: 'page-dom-ready',
      ...restAppDataRef.current,
      ...data
    });
  }, []);
  const dataLayerHandle = useCallback(() => getDataLayer(dataLayer()), [dataLayer]);
  const analyticHandlers = useMemo(() => ({
    ...defaultEvents,
    sendClick,
    sendEvent: eventBusSendEvent,
    sendImpressions,
    sendPage,
    sendThirdPartyEvent,
    sendDomReadyEvent,
    actualHandlers: {
      sendClick: sendClickHandle,
      sendEvent: sendEventHandle,
      sendImpressions: sendImpressionsHandle,
      sendPage: sendPageHandle,
      sendThirdPartyEvent: sendThirdPartyEventHandle,
      // TODO: This event is not ready to use yet! test in progress...
      sendDomReadyEvent: sendDomReadyEventHandle
    },
    dataLayer: dataLayerHandle
  }), [dataLayerHandle, sendClickHandle, sendDomReadyEventHandle, sendEventHandle, sendImpressionsHandle, sendPageHandle, sendThirdPartyEventHandle]);

  // if disabled, do not attempt to fire any AA tags
  if (disabled) {
    return defaultEvents;
  }
  return analyticHandlers;
}